import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import apiClient from "../../../../../helpers/api-client";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { convertPartialOptionsToNonNullable } from "@verity/proctoring-option";

export type ProctoringSession = {
	id: number;
	uuid: string;
	liveSessionId: string;
	userSessionId: string;
	idPhotoAvailable: boolean;
	roomScanVideoUrl?: string;
};

const proctoringSessionsApi = createApi({
	reducerPath: "proctoringSessions",
	baseQuery: async (arg, api, extraOptions) =>
		retry(
			fetchBaseQuery({
				fetchFn: apiClient.fetch.bind(apiClient),
				baseUrl: `/api/verity`,
			}),
			{ maxRetries: 10 },
		)(arg, api, extraOptions),
	tagTypes: ["ProctoringSession", "ProctoringSessionEvents"],
	endpoints: (builder) => ({
		getProctoringSessionByLiveUserSessionId: builder.query<ProctoringSession, string>({
			queryFn: async (userSessionId, _api, _extra, fetchWithBQ) => {
				const proctoringSessionResult = (await fetchWithBQ(`test-results/live-user-session/${userSessionId}`)) as {
					data?: Omit<ProctoringSession, "roomScanVideoUrl">;
				};
				const proctoringSessions = proctoringSessionResult.data;
				let roomScanVideoUrl: string | undefined;
				if (proctoringSessions) {
					roomScanVideoUrl = await apiClient.proctoringSession.getCachedRoomScanUrl(proctoringSessions.uuid);
				}
				return {
					...proctoringSessionResult,
					data: proctoringSessions
						? {
								...proctoringSessions,
								roomScanVideoUrl,
							}
						: undefined,
				} as any;
			},
			providesTags: (result) =>
				result
					? [
							{
								type: "ProctoringSession" as const,
								id: result.liveSessionId,
							},
						]
					: ["ProctoringSession"],
		}),
		getProctoringSessionConfiguration: builder.query<
			ProctoringOptionProfileV2["proctoringOption"] | undefined,
			{
				courseId: number;
				sessionId: string;
			}
		>({
			query: ({ courseId, sessionId }) => `quiz/event/${courseId}/${sessionId}`,
			transformResponse: (result: { events: { type: string; name: string; details: any }[] }) => {
				const options = result.events.find(({ type, name }) => type === "session" && name === "start proctoring")
					?.details?.options;
				return options ? convertPartialOptionsToNonNullable(options) : undefined;
			},
			providesTags: (result, _, { sessionId }) =>
				result
					? [
							{
								type: "ProctoringSessionEvents" as const,
								id: sessionId,
							},
						]
					: ["ProctoringSessionEvents"],
		}),
	}),
});

export default proctoringSessionsApi;
